import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import FormContent from "@/_components/FormContent"
import FormInputExtended from "@/_components/FormInputExtended"
import LayoutCard from "@/_components/LayoutCard"
import PanelInner from "@/_components/PanelInner"
import Table from "@/_components/Table"
import { filterData, getSearchParamName } from "@/_services/boardUtils"
import { copyEntity } from "@/_services/copyPaste"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
import { applyClasses, defaultTablePageSize, domAttributes, searchParamToObject } from "@/_services/utils"
import axios from "axios"
import { cloneDeep } from "lodash"
import React from "react"
import { Col, Row } from "react-bootstrap"
import { withRouter } from "react-router-dom"

class TableExtended extends React.Component {
  state = {
    page: 1,
    search: undefined,
    selectedRows: new Set(),
    rowsChildrenContent: new Map(),
    rowsLoading: new Set(),
  }

  componentDidMount() {
    const { location, collapseRows = true, data = [] } = this.props

    const { page } = searchParamToObject(location?.search)
    if (page !== undefined) this.setState({ page: Number(page) })
    if (!collapseRows && data.length) this.toggleAllRows()
  }

  componentDidUpdate(prevProps) {
    const { location, loading, collapseRows = true, data = [] } = this.props
    const { collapseRows: prevCollapseRows = true } = prevProps

    const { page } = searchParamToObject(location?.search)
    const { page: prevPage } = searchParamToObject(prevProps.location?.search)

    if (page !== undefined && page !== prevPage) {
      this.setState({ page: Number(page), selectedRows: new Set() })
    }
    if (data.length && ((prevProps.loading && !loading && !collapseRows) || prevCollapseRows !== collapseRows)) this.toggleAllRows()
  }

  toggleAllRows = () => {
    const { data = [] } = this.props
    for (const index in data) {
      const row = data[index]
      this.toggleRow(`${row._id || row.id || row.registration}|${index}`)
    }
  }

  toggleRow = async id => {
    const { selectedRows, rowsChildrenContent, rowsLoading } = this.state

    if (selectedRows.has(id)) selectedRows.delete(id)
    else {
      const rowIndex = Number(id.split("|")[1])
      let { data = [], loadRowChildren } = this.props

      if (loadRowChildren || (this.isUrl(data?.[rowIndex]?.children) && !rowsChildrenContent.has(id))) {
        // Row children can be defined as a url and fetched on demand
        rowsLoading.add(id)
        this.setState({ rowsLoading })

        try {
          if (loadRowChildren) {
            await loadRowChildren(data[rowIndex], rowIndex)
          } else {
            const content = (await axios.get(data[rowIndex].children)).data
            rowsChildrenContent.set(id, content)
          }
        } catch (error) {
          addOops(error)
        }

        rowsLoading.delete(id)
        this.setState({ rowsLoading })
      }

      selectedRows.add(id)
    }

    this.setState({ selectedRows })
  }

  handleSetPage = pageNumber => {
    const { changePage } = this.props
    if (changePage) changePage(pageNumber)
    this.setState({ page: pageNumber, selectedRows: new Set() })
  }

  handleSetSearch = search => {
    if (this.props.launchSearch) this.props.launchSearch(search)
    this.setState({ search, selectedRows: new Set() })
  }

  isUrl = str => typeof str === "string" && (str?.startsWith("/") || str?.startsWith("http"))

  // Determine if an action is enabled for a row. Indeed, some actions can be disabled for some rows only, using the `.action` field in the data.
  // The `.action` field can either be set as `false` or as a function returning a boolean to allow for greater flexibility.
  // The example below shows how to declare row-specific actions when the table is built based on `data: project.grantedFunds` :
  // Example : project.grantedFunds[0].action = { showDuplicate: false, showDelete: false }
  // Example : project.grantedFunds[0].action = { showDuplicate: project.grantedFunds.length < 5 }
  // Example : project.grantedFunds[0].action = { showDuplicate: ({row}) => row.amount !== 20000 }
  isActionEnabledForRow = (row, availableAction) => {
    // Check if availableAction global flag is a function
    if (typeof this.props[availableAction] === "function") return this.props[availableAction]({ row })
    // Check if availableAction global flag is set to `true` (it is considered disabled by default)
    if (this.props[availableAction] !== true) return false

    // Check if availableAction specific row flag is a function
    if (typeof row.action?.[availableAction] === "function") return row.action?.[availableAction]({ row })
    // Check if availableAction specific row flag is set to `false` (it is considered enabled by default)
    if (row.action?.[availableAction] === false) return false

    // Actions are enabled for all lines by default.
    return true
  }

  render() {
    let { search, selectedRows, page, rowsChildrenContent, rowsLoading } = this.state
    let {
      columns = [],
      data = [],
      rows = [],
      section,
      pageInUrl = false,
      pageSize = defaultTablePageSize,
      showAdd,
      showCopy,
      onRowDelete,
      onRowAdd,
      onRowDuplicate,
      rowChildrenDisplayMode,
      ...props
    } = this.props

    if (props.searchFromUrl) {
      const queryParams = searchParamToObject(props.location.search)
      search = queryParams[getSearchParamName(props.searchFromUrl)]
    }

    // __originalIndex contains the index of the row in the original array (the one passed via the "data" field).
    // It is returned to the onRowChange, onRowDelete, and onRowDuplicate events via the row._dataIndex parameter.
    // If the events manipulate the data, you must use row._dataIndex instead of row._index and rowIndex!
    // __originalIndex is essential when the data is filtered, because the data manipulated by the component is not the same as the data passed to the component.
    data = filterData(columns, data, search, { preserveOriginalIndex: true })

    const additionalPageRows = Array.from(selectedRows)
      .map(r => Number(r.split("|")[1]))
      .filter(index => index < page * pageSize && index >= (page - 1) * pageSize).length

    // Columns whose formInputProps have inArray: false are displayed among the row children
    const notInArrayColumns = columns.filter(col => col?.formInputProps?.inArray === false)
    columns = columns.filter(col => !notInArrayColumns.includes(col))

    const panels = rows.filter(row => row.type === "panel")

    const hasAtLeastOneRowWithChildren = data.findIndex(d => d?.children) !== -1
    if (hasAtLeastOneRowWithChildren || panels.length + notInArrayColumns.length > 0) {
      const expandedCol = columns.find(c => c?.name === "expanded")
      if (!expandedCol) {
        // For accessibility checks, header cells must always have something in them
        columns.unshift({ title: "Expand or collapse", name: "expanded", className: "max-w-0 w-1 min-w-24px invisible" })
      }
    }

    const { entity, onSetState } = this.props
    // Available actions at the end of each row
    const availableActions = {
      showDelete: {
        method: "DELETE",
        pullRight: true,
        onClick: async (args = {}) => {
          if (!args.row) return
          if (onRowDelete) {
            const { __originalIndex, ...rest } = args.row
            const patch = await onRowDelete({ ...args, row: { ...rest, _dataIndex: __originalIndex ?? args.row._index } })
            if (patch && onSetState) onSetState(patch)
            else if (onSetState) onSetState(entity) // for backward-compatibility
          } else {
            const index = args.row.__originalIndex ?? args.row._index
            this.props.data.splice(index, 1)
            onSetState && onSetState(entity)
          }
        },
      },
      showDuplicate: {
        tooltip: "Duplicate",
        icon: "icn-duplicate icn-xs",
        pullRight: true,
        onClick: async (args = {}) => {
          if (!args.row) return
          if (onRowDuplicate) {
            const { __originalIndex, ...rest } = args.row
            const patch = await onRowDuplicate({ ...args, row: { ...rest, _dataIndex: __originalIndex ?? args.row_index } })
            if (patch && onSetState) onSetState(patch)
            else if (onSetState) onSetState(entity) // for backward-compatibility
          } else {
            const index = args.row.__originalIndex ?? args.row._index
            this.props.data.splice(index + 1, 0, cloneDeep(this.props.data[index]))
            onSetState && onSetState(entity)
          }
        },
      },
      showCopy: {
        tooltip: "Copy",
        icon: "icn-copy icn-xs",
        pullRight: true,
        onClick: async (args = {}) => {
          if (!args.row) return
          copyEntity(React.isValidElement(args.row.expanded) ? { ...args.row, expanded: null } : args.row)
          addNotification("Done")
        },
      },
    }

    const onRowChange =
      typeof props.onRowChange === "function"
        ? props.onRowChange
        : props.useDefaultOnRowChange === true || section
          ? ({ row, patch }) => {
              const index = row.__originalIndex ?? row._index
              this.props.data[index] = { ...this.props.data[index], ...patch }
              if (entity) entity.isChanged = true
              return entity
            }
          : undefined

    data = data
      .map((row, rowIndex) => {
        // Add actions to the row, if enabled globally AND for the row
        const rowActions = []
        for (const availableAction of Object.keys(availableActions)) {
          if ((!this.props.readOnly || availableAction === "showCopy") && this.isActionEnabledForRow(row, availableAction)) {
            rowActions.push(availableActions[availableAction])
          }
        }
        if (rowActions.length) row.actions = rowActions

        if (row?.children || (row && panels.length) || (row && notInArrayColumns.length)) {
          const rowId = row._id || row.id || row.registration
          // Several rows can share the same registration (ex: the payment plan items of the same element),
          const rowKey = `${rowId ?? ""}|${row.__originalIndex ?? rowIndex}`
          const id = `${rowId}|${rowIndex}` // TODO : rowIndex --> row.__originalIndex ?
          const loading = rowsLoading.has(id)

          const componentFromColumn = col => {
            const { modelPath, modelSectionPath, execComputations } = this.props

            let _modelPath = col?.formInputProps?.modelPath || col?.modelPath
            // Compute modelPath if undefined (Ex: modelPath="persons[1].person" + modelSectionPath="employees" => "persons[1].person.employees[1]")
            if (!_modelPath && modelPath && modelSectionPath)
              _modelPath = [modelPath, `${modelSectionPath}[${rowIndex}]`].join(".") // TODO : rowIndex --> row.__originalIndex ?
            // Compute modelPath if undefined (Ex: modelPath="accounts" => "accounts[1]"); useful for forms in collapsed table rows
            else if (!_modelPath && modelPath) _modelPath = `${modelPath}[${rowIndex}]` // TODO : rowIndex --> row.__originalIndex ?

            if (React.isValidElement(row[col.name])) return row[col.name]

            return (
              <FormInputExtended
                key={col.name}
                inArray={false}
                obj={row}
                field={col.name}
                label={col.title}
                {...col}
                {...col.formInputProps}
                modelPath={_modelPath}
                onSetState={async patch => {
                  if (typeof props.onSetState !== "function") return
                  const { __originalIndex, ...rest } = row
                  // The pageState is returned to the onRowAdd, onRowDelete, onRowDuplicate, and onRowChange events so that Pages have access to the entity.
                  // To remain consistent across all row fields, it is also returned for fields with the "inArray" option set to false.
                  // PS: It is not necessary for layouts, as they already have access to the entity.
                  const rowPatch = await onRowChange({
                    pageState: cloneDeep(entity),
                    row: { ...rest, _index: rowIndex, _dataIndex: __originalIndex ?? rowIndex },
                    patch,
                    rowIndex,
                  })
                  if (rowPatch) props.onSetState(rowPatch, execComputations)
                }}
              />
            )
          }

          const hasId = selectedRows.has(id)
          const rows = [
            {
              ...row,
              __rowKey: rowKey,
              expanded: (
                <ButtonWithTooltip
                  simple
                  bsStyle="info"
                  bsSize="xs"
                  onClick={!loading && (() => this.toggleRow(id))}
                  tooltip={hasId ? "Collapse" : "Expand"}
                  {...{ [domAttributes.isExpanded]: hasId }}
                >
                  {loading && <i className="icn-circle-notch icn-spin icn-xs text-black-lightest" />}
                  {!loading && <i className={"icn-xs " + (hasId ? "icn-chevron-down" : "icn-chevron-right")} />}
                </ButtonWithTooltip>
              ),
              children: undefined,
            },
          ]

          if (hasId) {
            let childrenContent = <></>
            if (row.children) {
              if (typeof row.children === "string") {
                childrenContent = <LayoutCard noCard rows={[{ type: "content", ...rowsChildrenContent.get(id), noCard: false }]} />
              } else if (row.children.columns && row.children.data) {
                // eslint-disable-next-line no-unused-vars
                const { servicePagination, ...restProps } = props
                childrenContent = <TableExtended {...restProps} searchFromUrl={false} {...row.children} />
              } else if (row.children.fields) childrenContent = <FormContent {...row.children} />
              else childrenContent = row.children
            }

            if (row.children || notInArrayColumns.length)
              childrenContent = (
                <Row>
                  <Col xs={12}>{childrenContent}</Col>
                  {notInArrayColumns.map(componentFromColumn)}
                </Row>
              )

            const panelsContent = panels.map(panel => {
              return (
                <PanelInner key={panel.title} title={panel.title} collapse={true}>
                  {panel.rows.map((row, i) => {
                    return <Row key={`${panel.title}_row${i}`}>{row.map(componentFromColumn)}</Row>
                  })}
                </PanelInner>
              )
            })

            rows.push({
              __rowKey: `${rowKey}-panel`,
              className: "form-row",
              content: (
                <>
                  {childrenContent}
                  {panelsContent}
                </>
              ),
            })
          }
          return rows
        }
        return row
      })
      .flat()

    showAdd &&= !this.props.readOnly

    // Recreate a fresh actions column, else, pushing the add btn in the title will create a circular reference in the layout
    // and break conmparaisons throughout the client.
    let actionColumn = columns.find(col => col?.name === "actions")
    let actionColumnIndex = columns.indexOf(actionColumn)

    // Tot avoid mutations issues, remove the action column from the list, and rebuild it properly.
    if (actionColumn) columns = columns.filter(col => col !== actionColumn)

    // Rebuild actions columns and add it to the list of columns if needed.
    const title = actionColumn?.title || "actions"
    actionColumn = {
      title: typeof title === "string" ? loc(title) : title,
      name: actionColumn?.name || "actions",
      className: "w-5 text-right",
      sortable: false,
    }
    if (actionColumnIndex > -1 || (!this.props.readOnly && Object.keys(availableActions).some(action => this.props[action])) || showAdd || showCopy)
      columns.push(actionColumn)

    if (showAdd || showCopy)
      actionColumn.title = (
        <>
          {showAdd && (
            <CustomButton
              pullRight
              bsSize="xs"
              bsStyle="primary"
              className="inline-flex-center"
              simple
              onClick={async () => {
                if (onRowAdd) {
                  const rowPatch = await onRowAdd({ pageState: cloneDeep(entity) })
                  onSetState && onSetState(rowPatch || entity)
                } else {
                  this.props.data.push({})
                  onSetState && onSetState(entity)
                }
              }}
            >
              <i className="icn-plus icn-xs mr-5px" />
              {!showCopy && loc(`Add`)}
            </CustomButton>
          )}
          {showCopy && (
            <CustomButton
              pullRight
              bsSize="xs"
              bsStyle="info"
              className="inline-flex-center"
              simple
              onClick={() => {
                copyEntity(data)
                addNotification("Done")
              }}
            >
              <i className="icn-copy icn-xs" />
            </CustomButton>
          )}
        </>
      )

    // override onRowChange to remove expanded react element
    const _onRowChange = ({ row, ...args }) => {
      const { __originalIndex, expanded, ...rest } = row
      const formattedRow = { ...rest, _dataIndex: __originalIndex ?? row._index, expanded: React.isValidElement(expanded) ? null : expanded }
      return onRowChange({ row: formattedRow, ...args }) // args already contains pageState and rowIndex
    }

    return (
      <Table
        {...{ data, columns, pageInUrl, pageSize, additionalPageRows }}
        filterData={(cols, data) => data}
        handleSetSearch={this.handleSetSearch}
        handleSetPage={this.handleSetPage}
        {...props}
        // onRowChange can be undefined for uneditable fields
        onRowChange={onRowChange && _onRowChange}
        className={applyClasses({
          "table-extended": true,
          "children-condensed": rowChildrenDisplayMode === "condensed",
          [props.className]: props.className,
        })}
      />
    )
  }
}

export default withRouter(TableExtended)
